/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AbsoluteTime,
  Amounts,
  HttpStatusCode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  Loading,
  RenderAmount,
  Time,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { format } from "date-fns";
import { Fragment, VNode, h } from "preact";
import { State } from "./index.js";
import {
  revalidateConversionInfo,
  useConversionInfo,
} from "../../hooks/regional.js";
import { RetryableError } from "../RetryableError.js";

export function FailedView({ error }: State.Failed) {
  const { i18n } = useTranslationContext();
  switch (error.case) {
    case HttpStatusCode.NotImplemented: {
      return (
        <Attention type="danger" title={i18n.str`Cashout is disabled`}>
          <i18n.Translate>
            Cashout should be enabled in the configuration, the conversion rate
            should be initialized with fee(s), rates and a rounding mode.
          </i18n.Translate>
        </Attention>
      );
    }
    default:
      assertUnreachable(error.case);
  }
}

export function ReadyView({
  cashouts,
  failures,
  routeCashoutDetails,
  onRetryDetails,
}: State.Ready): VNode {
  const { i18n, dateLocale } = useTranslationContext();
  const conversionResp = useConversionInfo();

  if (!cashouts.length && failures.length === 0) {
    return (
      <div class="mt-6 rounded-lg border border-onBackground/10 bg-white p-6 text-sm text-gray-600">
        <i18n.Translate>No cashouts have been created yet.</i18n.Translate>
      </div>
    );
  }
  const txByDate = cashouts.reduce(
    (prev, cur) => {
      const d =
        cur.creation_time.t_s === "never"
          ? ""
          : format(cur.creation_time.t_s * 1000, "dd/MM/yyyy", {
              locale: dateLocale,
            });
      if (!prev[d]) {
        prev[d] = [];
      }
      prev[d].push(cur);
      return prev;
    },
    {} as Record<string, typeof cashouts>,
  );
  if (!conversionResp) {
    return <Loading />;
  } else if (conversionResp instanceof TalerError) {
    return (
      <RetryableError
        error={conversionResp}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => revalidateConversionInfo()}
      />
    );
  } else if (conversionResp.type === "fail") {
    switch (conversionResp.case) {
      case HttpStatusCode.NotImplemented: {
        return (
          <Attention type="danger" title={i18n.str`Cashout is disabled`}>
            <i18n.Translate>
              Cashout should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      }
      default:
        assertUnreachable(conversionResp);
    }
  }
  const { fiat_currency_specification, regional_currency_specification } =
    conversionResp.body;

  return (
    <div class="mt-6">
      {failures.length > 0 && (
        <Attention
          type="danger"
          title={i18n.str`Some cashout details could not be loaded.`}
        >
          <p>
            <i18n.Translate>
              The partial list omits cashout IDs:{" "}
              {failures.map(({ id }) => id).join(", ")}.
            </i18n.Translate>
          </p>
          <button
            type="button"
            class="mt-2 font-semibold underline"
            onClick={onRetryDetails}
          >
            <i18n.Translate>Try again</i18n.Translate>
          </button>
        </Attention>
      )}
      <div class="space-y-4 sm:hidden">
        {Object.entries(txByDate).map(([date, txs]) => (
          <section key={date} aria-label={date}>
            <h2 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {date}
            </h2>
            <div class="space-y-3">
              {txs.map((item) => (
                <article
                  key={item.id}
                  class="rounded-lg border border-onBackground/10 bg-white p-4 shadow-sm"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <div class="font-medium text-onBackground">
                        {item.subject}
                      </div>
                      <div class="mt-1 text-xs text-gray-500">
                        <Time
                          format="HH:mm:ss"
                          timestamp={AbsoluteTime.fromProtocolTimestamp(
                            item.creation_time,
                          )}
                        />
                      </div>
                    </div>
                    <a
                      data-name="cashout details"
                      class="shrink-0 text-sm font-semibold text-brand hover:underline"
                      href={routeCashoutDetails.url({
                        cid: String(item.id),
                      })}
                    >
                      <i18n.Translate>View details</i18n.Translate>
                    </a>
                  </div>
                  <dl class="mt-4 grid grid-cols-2 gap-3 border-t border-onBackground/10 pt-3 text-sm">
                    <div>
                      <dt class="text-xs text-gray-500">
                        <i18n.Translate>Debited</i18n.Translate>
                      </dt>
                      <dd class="mt-1 font-medium text-red-600">
                        <RenderAmount
                          value={Amounts.parseOrThrow(item.amount_debit)}
                          negative
                          withSign
                          spec={regional_currency_specification}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs text-gray-500">
                        <i18n.Translate>Transferred</i18n.Translate>
                      </dt>
                      <dd class="mt-1 font-medium text-green-600">
                        <RenderAmount
                          value={Amounts.parseOrThrow(item.amount_credit)}
                          spec={fiat_currency_specification!}
                        />
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div class="hidden overflow-x-auto rounded-lg border border-onBackground/10 bg-white sm:block">
        <table class="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th
                scope="col"
                class="pl-3 py-3.5 text-left text-sm font-semibold text-onBackground"
              >{i18n.str`Created`}</th>
              <th
                scope="col"
                class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
              >{i18n.str`Debited`}</th>
              <th
                scope="col"
                class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
              >{i18n.str`Transferred`}</th>
              <th
                scope="col"
                class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
              >{i18n.str`Transfer reference`}</th>
              <th scope="col" class="relative py-3.5 pl-3 pr-4">
                <span class="sr-only">
                  <i18n.Translate>Actions</i18n.Translate>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(txByDate).map(([date, txs]) => {
              return (
                <Fragment key={date}>
                  <tr class="border-t border-gray-200">
                    <th
                      colSpan={5}
                      scope="colgroup"
                      class="bg-gray-50 py-2 pl-4 pr-3 text-left text-sm font-semibold text-onBackground sm:pl-3"
                    >
                      {date}
                    </th>
                  </tr>
                  {txs.map((item) => {
                    return (
                      <tr
                        key={item.id}
                        class="border-b border-gray-200 last:border-none"
                      >
                        <td class="whitespace-nowrap py-3 pl-3 pr-2 text-sm text-onBackground">
                          <Time
                            format="HH:mm:ss"
                            timestamp={AbsoluteTime.fromProtocolTimestamp(
                              item.creation_time,
                            )}
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-3 text-sm text-red-600">
                          <RenderAmount
                            value={Amounts.parseOrThrow(item.amount_debit)}
                            negative
                            withSign
                            spec={regional_currency_specification}
                          />
                        </td>
                        <td class="whitespace-nowrap px-3 py-3 text-sm text-green-600">
                          <RenderAmount
                            value={Amounts.parseOrThrow(item.amount_credit)}
                            spec={fiat_currency_specification!}
                          />
                        </td>

                        <td class="max-w-xs break-words px-3 py-3 text-sm text-gray-600">
                          {item.subject}
                        </td>
                        <td class="whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm">
                          <a
                            data-name="cashout details"
                            class="font-semibold text-brand hover:underline"
                            href={routeCashoutDetails.url({
                              cid: String(item.id),
                            })}
                          >
                            <i18n.Translate>View details</i18n.Translate>
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
